import { forwardRef } from 'react';
import type { DepartmentOption } from '../../../people/types';
import type { ControlA11y } from '../../guidance/GuidedField';
import { GroupedCombobox, type ComboGroup, type ComboLoadResult, type GroupedComboboxHandle } from './GroupedCombobox';
import { referenceLoader } from './referenceOptions';

/**
 * The Department field: the company's active departments, "Not set", the recommended standard department names that the company does not have yet
 * (each opens the Add department dialog with the name filled in) and "+ Add new department". Nothing is assigned for the person.
 *
 * `canCreate` comes from the capabilities endpoint (a hint; the server enforces it). When it is false there is no action to add a department, only
 * the sentence that tells the person who can. While it is still unknown (`null`) the actions are offered, because the server will refuse them if needed.
 */

export const NO_DEPARTMENT_RIGHTS_TEXT =
  'Ask a company administrator who can manage employees to add a department. You can save this role without one, and choose a department later.';

export interface DepartmentFieldProps {
  a11y: ControlA11y;
  /** The department id ('' for none). */
  value: string;
  departments: DepartmentOption[];
  canCreate: boolean | null;
  templateId?: string | null;
  disabled?: boolean;
  onSelect: (departmentId: string) => void;
  /** Opens the Add department dialog; `name` is a recommended standard name or what was typed. */
  onRequestAdd: (name: string) => void;
}

export const DepartmentField = forwardRef<GroupedComboboxHandle, DepartmentFieldProps>(function DepartmentField(
  { a11y, value, departments, canCreate, templateId, disabled, onSelect, onRequestAdd }, ref,
) {
  const active = departments.filter((d) => d.isActive);
  const selected = departments.find((d) => d.id === value);
  const mayCreate = canCreate !== false;
  const loadRecommended = referenceLoader('RecommendedDepartment', templateId);

  async function load(query: string, signal: AbortSignal): Promise<ComboLoadResult> {
    const q = query.toLowerCase();
    const groups: ComboGroup[] = [];
    if (!q) groups.push({ key: 'none', label: 'No department', options: [{ value: '', label: 'Not set', detail: 'The role does not belong to a department yet.' }] });
    const mine = active.filter((d) => !q || d.name.toLowerCase().includes(q) || (d.code ?? '').toLowerCase().includes(q));
    if (mine.length > 0) {
      groups.push({ key: 'company', label: 'Your company’s departments', options: mine.map((d) => ({ value: d.id, label: d.name, detail: d.code ? `Code ${d.code}` : 'No code' })) });
    }
    if (mayCreate) {
      try {
        const standard = await loadRecommended(query, signal);
        const have = new Set(departments.map((d) => d.name.toLowerCase()));
        const names = standard.groups.flatMap((g) => g.options).map((o) => o.label).filter((n, i, all) => all.indexOf(n) === i && !have.has(n.toLowerCase()));
        if (names.length > 0) {
          groups.push({
            key: 'standard', label: 'Not in your company yet: add one of these',
            options: names.slice(0, 8).map((n) => ({ value: n, label: `Create “${n}”…`, actionId: `create:${n}` })),
          });
        }
      } catch {
        if (signal.aborted) throw new Error('cancelled');
        /* the list of standard names is a convenience: without it the other choices still work */
      }
      groups.push({ key: 'actions', label: 'Or', options: [{ value: '+add', label: '+ Add new department', actionId: 'add' }] });
    }
    return { groups };
  }

  return (
    <div className="jr-department">
      <GroupedCombobox
        ref={ref}
        a11y={a11y}
        mode="select"
        value={value}
        valueLabel={selected ? selected.name : value ? 'A department that is no longer active (kept)' : ''}
        load={load}
        onSelect={(o) => onSelect(o.value)}
        onClear={() => onSelect('')}
        clearLabel="Department"
        onAction={(actionId, typed) => onRequestAdd(actionId.startsWith('create:') ? actionId.slice('create:'.length) : typed)}
        disabled={disabled ?? false}
        placeholder="Search your departments"
        listFooter={!mayCreate ? <span>{NO_DEPARTMENT_RIGHTS_TEXT}</span> : undefined}
        emptyText="No department matches. Try another word."
      />
      {canCreate === false && <p className="jr-hint" data-testid="department-no-create">{NO_DEPARTMENT_RIGHTS_TEXT}</p>}
    </div>
  );
});
